/**
 * The account's own key — the one thing a hostile server must not be able to
 * speak with.
 *
 * A device key says "this device". The master key says "this account", and it
 * is what signs the list of devices the account admits to owning. Peers pin
 * THIS, not the per-device keys, which is why adding a phone stops being an
 * event other people have to adjudicate: the list moves underneath a key they
 * already trust.
 *
 * It lives on one device, not on all of them, and that is a deliberate cost.
 * Signal copies its account identity to every linked device; that makes
 * linking easy and makes one stolen laptop the whole account. Keeping it in
 * one place means the device that holds it has to take part in authorising
 * another — which is the same shape as approving a linked device by QR, and
 * an honest place for a person to be asked.
 *
 * Because of that it must be recoverable, so it belongs in the passphrase-
 * protected history archive: losing the phone that holds it should cost the
 * ability to add devices, not the account.
 */

import nacl from 'tweetnacl';
import { b64enc, b64dec } from './identity.mjs';

const MASTER_KEY = 'voxiox_master_v1';

const scoped = (accountId) => (accountId == null ? MASTER_KEY : `${MASTER_KEY}:${accountId}`);

/**
 * Load the account master key, or make one.
 *
 * `create` is false wherever the answer "there isn't one" is meaningful — a
 * device that is not the one holding the master must not quietly mint a second
 * master and start signing lists nobody pinned.
 */
export function masterKeyPair(storage, accountId, { create = false } = {}) {
  const key = scoped(accountId);
  const stored = storage.getItem(key);
  if (stored) {
    try {
      const parsed = JSON.parse(stored);
      const secret = b64dec(parsed.secretKey);
      if (secret.length === nacl.sign.secretKeyLength) return nacl.sign.keyPair.fromSecretKey(secret);
    } catch (e) { /* fall through and treat as absent */ }
  }
  if (!create) return null;
  const kp = nacl.sign.keyPair();
  storage.setItem(key, JSON.stringify({
    publicKey: b64enc(kp.publicKey), secretKey: b64enc(kp.secretKey), createdAt: 0,
  }));
  return kp;
}

/** The public half, for pinning and for the list header. Null if we hold none. */
export function masterPublicKey(storage, accountId) {
  const kp = masterKeyPair(storage, accountId, { create: false });
  return kp ? b64enc(kp.publicKey) : null;
}

export function hasMaster(storage, accountId) {
  return !!storage.getItem(scoped(accountId));
}

/**
 * Adopt a master key that arrived from elsewhere — a restored archive, or a
 * transfer from the device that held it.
 *
 * Refuses to overwrite an existing one. Two masters for one account is the
 * state where half the peers pin one and half the other, and nothing on any
 * screen would explain it.
 */
export function adoptMaster(storage, accountId, secretKeyB64) {
  if (hasMaster(storage, accountId)) return { ok: false, reason: 'этот аккаунт уже имеет мастер-ключ' };
  let kp;
  try {
    kp = nacl.sign.keyPair.fromSecretKey(b64dec(secretKeyB64));
  } catch (e) { return { ok: false, reason: 'ключ не читается' }; }
  storage.setItem(scoped(accountId), JSON.stringify({
    publicKey: b64enc(kp.publicKey), secretKey: b64enc(kp.secretKey), createdAt: 0,
  }));
  return { ok: true, publicKey: b64enc(kp.publicKey) };
}

/** For the archive. The secret itself — treat the result as the account. */
export function exportMaster(storage, accountId) {
  const kp = masterKeyPair(storage, accountId, { create: false });
  return kp ? b64enc(kp.secretKey) : null;
}

export { MASTER_KEY };
